import {
    NavigationComponents,
    NavigationState,
    Route,
} from '../../navigation/types'
import Loader from 'components/Loader'
import Main from '../Main'
import {PrivateNavigatorErrorBoundary} from 'components/ErrorBoundary'
import React from 'react'
import permissionGuard from './helpers/PermissionGuard'
import router from '../../navigation/router'
import {usePermissions} from 'hooks/permissions'
import {useRouting} from '../../navigation/hooks'

const components: NavigationComponents = {
    Accept: React.lazy(() => import('../Accept')),
    Channel: React.lazy(() => import('../Channel')),
    ChannelSettings: React.lazy(() => import('../ChannelSettings')),
    Dialogue: React.lazy(() => import('../Dialogue')),
    EmbedChannel: () => null,
    EmbedHome: () => null,
    EmbedLogin: () => null,
    EmbedPostQuestionnaire: () => null,
    EmbedQuestionnaire: () => null,
    EmbedQueue: () => null,
    File: React.lazy(() => import('../File')),
    ForgotPassword: React.lazy(() => import('../ForgotPassword')),
    Home: React.lazy(() => import('../Home')),
    Login: React.lazy(() => import('../Login')),
    LoginSign: React.lazy(() => import('../LoginSign')),
    ...process.env.MODE === 'development' ? {Playground: React.lazy(() => import('../../playground/'))} : {Playground: () => <div />},
    PermissionDenied: React.lazy(() => import('../PermissionDenied')),
    Queue: React.lazy(() => import('../Queue')),
    QueueSettings: React.lazy(() => import('../QueueSettings')),
    QueueStatistics: React.lazy(() => import('../QueueStatistics')),
    Realm: React.lazy(() => import('../Realm')),
    Register: React.lazy(() => import('../Register')),
    RegisteredAudience: React.lazy(() => import('../Transcript')),
    ResetPassword: React.lazy(() => import('../ForgotPassword')),
    SSOBegin: React.lazy(() => import('../SSOBegin')),
    SSOLogin: React.lazy(() => import('../SSOLogin')),
    ScheduledAppointments: React.lazy(() => import('../ScheduledAppointments')),
    Settings: React.lazy(() => import('../Settings')),
    Transcript: React.lazy(() => import('../Transcript')),
}

interface NavigatorProps {
    hasSession: boolean
    navigationState: NavigationState
}

type SubNavigatorProps = NavigationState

const PublicNavigator = (props: SubNavigatorProps) => {
    const {
        position,
        routes,
    } = props

    const route = position < routes.length ? routes[position] : routes[routes.length - 1]

    const {
        isPending,
        pushRoute,
    } = useRouting()

    if (isPending) {
        return <Loader />
    }

    if (!route) {
        pushRoute({name: 'Login'})
        return <Loader />
    }

    if (router.isPrivate(route.name)) {
        pushRoute({name: 'Login'})
        return <Loader />
    }

    const Component = components[route.name]

    return <Component params={route.params as never} />
}

const PrivateNavigator = (props: SubNavigatorProps) => {
    const {
        position,
        routes,
    } = props

    const [initialRoute] = routes
    const route = position < routes.length ? routes[position] : routes[routes.length - 1]

    const {
        isPending,
        popModal,
        pushRoute,
        replaceRoute,
    } = useRouting()

    const fallbackRoute: Route = {name: 'Home'}

    const {loading: permissionLoading} = usePermissions(route as Route)

    if (isPending) {
        return <Loader />
    }

    if (!route) {
        pushRoute(fallbackRoute)
        return <Loader />
    }

    if (permissionLoading) {
        // need to render empty Main to keep layout and jitsi stable while permissions are loading
        return (
            <Main childName={route.name} hidden routeParams={route.params} ><span /></Main>)
    }

    if (!permissionGuard.isAllowedRoute()) {
        return <Loader />
    }

    if (router.isPublic(route.name)) {
        // not execute replace route for login-sign route, for situations when it its requested from already logged user
        if (route.name !== 'LoginSign') {
            const nextRoute = initialRoute && router.isPrivate(initialRoute.name) ? initialRoute : fallbackRoute
            replaceRoute(nextRoute)
            return <Loader />
        }
    }

    if (router.isModal(route)) {
        popModal(route)
        const previousRoute = routes
            .slice(0, position)
            .reverse()
            .find((r) => r && !router.isModal(r)) ?? null
        replaceRoute(previousRoute ?? fallbackRoute)
        return <Loader />
    }

    const errorBoundaryKey = router.stringify(route.name, route.params) ?? route.name
    const Component = components[route.name]

    return (
        <Main childName={route.name} routeParams={route.params}>
            <PrivateNavigatorErrorBoundary currentRoute={route} fallbackRoute={fallbackRoute} key={errorBoundaryKey}>
                <Component params={route.params as never} />
            </PrivateNavigatorErrorBoundary>
        </Main>
    )
}

const Navigator = (props: NavigatorProps) => {
    const {
        hasSession,
        navigationState: {
            position,
            routes,
        },
    } = props

    const SubNavigator = hasSession ? PrivateNavigator : PublicNavigator

    return <SubNavigator position={position} routes={routes} />
}

export default Navigator
